import { Component } from '@angular/core';
import { AccountCenterService } from '../../../../../../coverpath-common/angular/services/account-center/account-center.service';
import { AmorphicService } from '../../../../../../coverpath-common/angular/services/amorphic/amorphic.service';
import {
  PolicyLink,
  PolicyModal,
} from '../../../../../../coverpath-common/angular/interfaces/account-center/PolicyLink';
import { CoverpathPolicyLinkConstructor } from '../../../classes/policy-links/CoverpathPolicyLinkConstructor';
import { ModalService } from '../../../../../../coverpath-common/angular/services/modal/modal.service';
import { BasePolicyLinksComponent } from '../../../../../../coverpath-common/angular/components/account-center/policy-links/base-policy-links.component';
import { CoverpathUtils } from '../../../../../../coverpath-common/js/static/CoverpathUtils';
import { SanderlingProductCodes } from '../../../../../../../apps/common/js/static/Assumptions';
import { PolicyData } from '../../../../../../coverpath-common/angular/interfaces/PolicyData';
import { PaymentMethodType } from '@hit/policy-admin-types/dist/data-model/Types';
import { Controller } from '../../../Controller';

@Component({
  selector: 'policy-links',
  moduleId: module.id,
  templateUrl: './policy-links.component.html',
})
export class PolicyLinksComponent extends BasePolicyLinksComponent {
  controller;

  ngOnInit(): void {
    this.subscribeToAccountCenterService();
  }

  constructor(
    accountCenterService: AccountCenterService,
    amorphicService: AmorphicService,
    modalService: ModalService
  ) {
    super(accountCenterService, amorphicService, modalService);
    this.controller = this.amorphicService.controller as Controller;
  }

  async constructLinksForPolicyMode(workflowAction: string): Promise<void> {
    const primaryPolicyNumber = this.accountCenterService.primaryPolicy.policyNumber;
    const primaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(
      this.accountCenterService.primaryPolicy.__id__ as string
    );
    let secondaryPolicyData;

    if (primaryPolicyData.getCACIsJointApp()) {
      const secondaryPolicyId = this.accountCenterService.application.secondaryPolicyWithPendingAmendment.__id__;
      secondaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(secondaryPolicyId);
    }

    const hideBankAccountWidget =
      primaryPolicyData.getCACPaymentData().getCACNeedsAuthorizationWithinOrPast() ||
      secondaryPolicyData?.getCACPaymentData().getCACNeedsAuthorizationWithinOrPast();

    const isConversionInProgress: boolean = !!primaryPolicyData.getCACConvertedTo();
    const isSanderlingPolicy = SanderlingProductCodes.includes(this.accountCenterService.primaryPolicy.product);
    this.topLinks = new Array<PolicyLink>();
    this.bottomLinks = new Array<PolicyLink>();

    //show a loader if this is the first time rendering the component so we don't have a short-term blank component
    if (this.topLinks.length === 0 && this.bottomLinks.length === 0) {
      this.isLoading = true;
    }

    const isPolicyHeld = await this.accountCenterController.getPAStatusIsPolicyHeld(primaryPolicyNumber);
    const specialHandling = await this.accountCenterController.getPAStatusSpecialHandling(primaryPolicyNumber);
    const canUseSelfServiceWidgets =
      !isPolicyHeld && !specialHandling && !this.accountCenterService.application.primaryPolicy.isJointOwnership();

    if (isSanderlingPolicy) {
      if (!hideBankAccountWidget) {
        this.setBankAccountWidget(canUseSelfServiceWidgets, primaryPolicyData, isSanderlingPolicy);
      }
      this.setBeneWidget(canUseSelfServiceWidgets, primaryPolicyData, isSanderlingPolicy);
      this.setContactWidget(canUseSelfServiceWidgets, primaryPolicyData, isSanderlingPolicy);
    } else {
      this.topLinks.push(new PolicyLink('View Application PDF', this.accountCenterController.getViewApplicationURL()));
      if (this.accountCenterService.application.isJointApp()) {
        this.topLinks.push(new PolicyModal('View Policy', 'policyDocsJointSplit'));
      } else {
        this.topLinks.push(
          new PolicyLink('View Policy', this.accountCenterController.getViewPolicyAdminDocsURL('primary'))
        );
      }

      if (this.accountCenterService.shouldHaveAnnualStatement()) {
        this.topLinks.push(
          new PolicyLink('View Annual Statement', this.accountCenterController.getViewPolicyAdminAnnualStatementURL())
        );
      }

      //if conversion is in progress, only show the above links
      if (isConversionInProgress) {
        this.isLoading = false;
        return;
      }

      //Bank Account
      if (!hideBankAccountWidget) {
        this.setBankAccountWidget(canUseSelfServiceWidgets, primaryPolicyData, false);
      }

      //Beneficiaries
      this.setBeneWidget(canUseSelfServiceWidgets, primaryPolicyData, false);

      //Owner
      this.topLinks.push(new PolicyLink('Change Owner', CoverpathPolicyLinkConstructor.creatChangeOwnerLink()));

      //Contact Info
      this.setContactWidget(canUseSelfServiceWidgets, primaryPolicyData);

      //Name
      this.topLinks.push(
        new PolicyLink(
          'Change Name',
          CoverpathPolicyLinkConstructor.createChangeNameLink(this.controller.cpClientChangeNamePowerFormId)
        )
      );

      //Face Amount
      this.topLinks.push(
        new PolicyLink(
          'Change Face Amount',
          CoverpathPolicyLinkConstructor.createChangeFaceAmountLink(this.controller.cpClientChangeFaceAmountPowerFormId)
        )
      );

      //Convert Policy
      if (primaryPolicyData.getCACIsTermLifePolicy() || primaryPolicyData.getCACIsJointApp()) {
        this.topLinks.push(new PolicyModal('Convert Policy', 'convertPolicy'));
      }

      //Dividend Option
      if (primaryPolicyData.getCACIsWholeLife()) {
        this.topLinks.push(new PolicyModal('Change Dividend Option', 'changeDividendOption'));
      }

      //Cancel
      if (/Pending Policy Docs/.test(workflowAction)) {
        // skip any cancellation link for these stages
      } else {
        if (CoverpathUtils.isInForce(workflowAction)) {
          this.bottomLinks.push(new PolicyModal('Cancel Policy', 'cancelPolicy'));
        } else {
          this.bottomLinks.push(new PolicyModal('Cancel Policy', 'cancelPolicySimple'));
        }
      }
    }
    this.isLoading = false;
  }

  private setBankAccountWidget(
    canUseSelfServiceWidgets: boolean,
    primaryPolicyData: PolicyData,
    isSanderlingPolicy: boolean
  ) {
    const cacPaymnetData = primaryPolicyData.getCACPaymentData();
    if (
      canUseSelfServiceWidgets &&
      cacPaymnetData.getCACPaymentMode() === 'ach' &&
      (cacPaymnetData.getCACPaymentFrequency() === 'annual' || cacPaymnetData.getCACPaymentFrequency() === 'monthly')
    ) {
      const bankInfoModalName = primaryPolicyData.getCACIsJointApp() ? 'bankInfoJointSplit' : 'bankInfo';
      this.topLinks.push(new PolicyModal('Change Payment Method', bankInfoModalName));
    } else if (isSanderlingPolicy) {
      this.topLinks.push(new PolicyModal('Change Payment Method', 'changeSanderlingDetail'));
    } else {
      this.topLinks.push(
        new PolicyLink('Change Payment Method', CoverpathPolicyLinkConstructor.createBankAccountLink())
      );
    }
  }

  private setBeneWidget(canUseSelfServiceWidgets, primaryPolicyData, isSanderlingPolicy) {
    const ownerResidentialState = primaryPolicyData.getCACOwnerPersonResidentialAddressState();
    const canUseBeneWidget = canUseSelfServiceWidgets && ownerResidentialState !== 'MA';
    if (canUseBeneWidget) {
      const beneWidgetModalName = primaryPolicyData.getCACIsJointApp() ? 'beneWidgetJointSplit' : 'beneWidget';
      this.topLinks.push(new PolicyModal('Change Beneficiary', beneWidgetModalName));
    } else if (isSanderlingPolicy) {
      this.topLinks.push(new PolicyModal('Change Beneficiary', 'changeSanderlingDetail'));
    } else {
      this.topLinks.push(
        new PolicyLink(
          'Change Beneficiary',
          CoverpathPolicyLinkConstructor.createChangeBeneficiaryLink(ownerResidentialState)
        )
      );
    }
  }

  private setContactWidget(
    canUseSelfServiceWidgets: boolean,
    primaryPolicyData: PolicyData,
    isSanderlingPolicy?: boolean
  ) {
    if (canUseSelfServiceWidgets) {
      const contactWidgetModalName = primaryPolicyData.getCACIsJointApp() ? 'contactWidgetJointSplit' : 'contactWidget';
      this.topLinks.push(new PolicyModal('Update Contact Information', contactWidgetModalName));
    } else if (isSanderlingPolicy) {
      this.topLinks.push(new PolicyModal('Update Contact Information', 'changeSanderlingDetail'));
    } else {
      this.topLinks.push(
        new PolicyLink(
          'Update Contact Information',
          CoverpathPolicyLinkConstructor.createMailingAddressLink(this.controller.cpClientChangeContactInfoPowerFormId)
        )
      );
    }
  }
}
